import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {
    OtherReservationListRequestDTO,
    OtherReservationListRowDTO,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";
import {
    type Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationAggregationService} from "crqlar-react-common/dist/services/aggregation/reservationAggregation.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {addMinutes, endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useReservationFilterOptions} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import RangeDatePicker from "../../common/newDatePicker/RangeDatePicker";
import {useDebouncedDateRange} from "../../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailExport,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";
import {AliasAgeCategoriesElement} from "../../common/uiElements/AliasAgeCategoriesElement";
import {CountryElement} from "../../common/uiElements/CountryElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {NationalityElement} from "../../common/uiElements/NationalityElement";
import {NotesElement} from "../../common/uiElements/NotesElement";
import {noteTexts} from "crqlar-react-common/dist/utils/noteTypeGroup.util";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";
import {SimpleChipElement} from "../../common/uiElements/SimpleChipElement";
import {ReservationResourcePicker} from "../rail/ReservationResourcePicker";
import {useReservationResourceSelection} from "../rail/useReservationResourceSelection";
import ReservationDialog from "../reservationDialog/ReservationDialog";
import {createReservationUpdatedColumn} from "./reservationUpdatedColumn";

const displayName = (row: OtherReservationListRowDTO) => (
    row.fullName ?? [row.firstName, row.lastName].filter(Boolean).join(" ")
);

const DateTimeElement = ({row}: {row: OtherReservationListRowDTO}) => (
    <Box sx={{display: "flex", flexDirection: "column", gap: 0.25}}>
        <DateElement date={row.start}/>
        <DateElement date={row.end}/>
    </Box>
);

export default function OtherReservationList({sectorId}: {sectorId: string}) {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const filterOptionsQuery = useReservationFilterOptions();
    const ageCategories = useAllAgeCategories();
    const resourceSelection = useReservationResourceSelection({sectorId, viewKey: "list"});
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const {range: dateRange, queryRange, onRangeChange} = useDebouncedDateRange(sectorId);
    const selectedResourceIds = resourceSelection.resources.length === 0 || resourceSelection.allSelected
        ? undefined
        : resourceSelection.selectedIds;

    const handleOpenReservation = useCallback(async (row: OtherReservationListRowDTO) => {
        try {
            const reservation = await ReservationService.getReservationById(row.id);
            if (!reservation) throw new Error("Reservation not found");
            setSelectedReservation(reservation);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {variant: "error"});
        }
    }, [t]);

    const handleCreateReservation = useCallback(() => {
        const now = new Date();
        const start = new Date(dateRange.start);
        start.setHours(now.getHours(), now.getMinutes(), 0, 0);

        setSelectedReservation({
            expectedStartDate: start,
            expectedEndDate: addMinutes(start, 60),
            sectorId,
            status: ReservationStatus.CONFIRMED,
        });
    }, [dateRange.start, sectorId]);

    const columns = useMemo<ColumnDef<OtherReservationListRowDTO>[]>(() => [
        {
            id: "guest",
            header: t("Primary guest"),
            accessorFn: displayName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 220,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "countryCode",
            header: t("Country"),
            accessorFn: row => row.countryCode,
            cell: ({row}) => <CountryElement countryCode={row.original.countryCode}/>,
            size: 130,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => <CountryElement countryCode={row.original.countryCode} asPrint/>,
            },
        },
        {
            id: "nationality",
            header: t("Nationality"),
            accessorFn: row => row.nationality,
            cell: ({row}) => <NationalityElement nationality={row.original.nationality}/>,
            size: 130,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => <NationalityElement nationality={row.original.nationality} asPrint/>,
            },
        },
        {
            id: "languageCodes",
            header: t("Language"),
            accessorFn: row => row.languageCodes ?? [],
            cell: ({row}) => <LanguagesElement languageCodes={row.original.languageCodes}/>,
            size: 120,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => <LanguagesElement languageCodes={row.original.languageCodes} asPrint/>,
            },
        },
        {
            id: "dateTime",
            header: t("Date & Time"),
            accessorFn: row => row.start,
            cell: ({row}) => <DateTimeElement row={row.original}/>,
            size: 190,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "start",
                printable: true,
                printDefault: true,
                printCell: row => <DateTimeElement row={row.original}/>,
            },
        },
        {
            id: "serviceName",
            header: t("Service"),
            accessorFn: row => row.serviceName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 260,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                grow: true,
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "status",
            header: t("Status"),
            accessorFn: row => row.status,
            cell: ({row}) => (
                <ReservationStatusElement
                    status={row.original.status}
                    start={row.original.start}
                    end={row.original.end}
                />
            ),
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <ReservationStatusElement status={row.original.status} asPrint/>,
            },
        },
        {
            id: "notes",
            header: t("Notes"),
            accessorFn: row => noteTexts(row.notes),
            cell: ({row}) => <NotesElement notes={row.original.notes}/>,
            size: 280,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => <NotesElement notes={row.original.notes} asPrint/>,
            },
        },
        {
            id: "guestCount",
            header: t("Guest count abbreviation"),
            accessorFn: row => row.guestCount,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 120,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "guests",
            header: t("Age categories"),
            accessorFn: row => row.guestCount ?? 0,
            cell: ({row}) => (
                <AgeCategoriesElement
                    ageCategories={ageCategories}
                    birthdays={row.original.birthday ? [row.original.birthday] : []}
                    referenceDate={row.original.start ?? queryRange.start}
                    computedAgeCategories={row.original.computedAgeCategories}
                />
            ),
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "computedAgeCategories",
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AgeCategoriesElement
                        ageCategories={ageCategories}
                        birthdays={row.original.birthday ? [row.original.birthday] : []}
                        referenceDate={row.original.start ?? queryRange.start}
                        computedAgeCategories={row.original.computedAgeCategories}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "aliasAgeCategories",
            header: `${t("Age categories")} (${t("Alias")})`,
            accessorFn: row => row.aliasAgeCategories,
            cell: ({row}) => (
                <AliasAgeCategoriesElement
                    aliasAgeCategories={row.original.aliasAgeCategories}
                    ageCategories={ageCategories}
                />
            ),
            size: 160,
            meta: {
                defaultActive: false,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AliasAgeCategoriesElement
                        aliasAgeCategories={row.original.aliasAgeCategories}
                        ageCategories={ageCategories}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "source",
            header: t("Source"),
            accessorFn: row => row.fromBookingEngine,
            cell: ({row}) => t(row.original.fromBookingEngine ? "Online Reservation" : "Staff"),
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "fromBookingEngine",
                printable: true,
                printDefault: true,
                printCell: row => t(row.original.fromBookingEngine ? "Online Reservation" : "Staff"),
            },
        },
        {
            id: "resourceCategories",
            header: t("Resource categories"),
            accessorFn: row => row.resourceCategoryNames ?? [],
            cell: ({row}) => <SimpleChipElement values={row.original.resourceCategoryNames}/>,
            size: 240,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "resourceCategoryNames",
                printable: true,
                printDefault: true,
                printCell: row => <SimpleChipElement values={row.original.resourceCategoryNames} asPrint/>,
            },
        },
        {
            id: "resources",
            header: t("Resources"),
            accessorFn: row => row.resourceNames ?? [],
            cell: ({row}) => <SimpleChipElement values={row.original.resourceNames}/>,
            size: 260,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "resourceNames",
                printable: true,
                printDefault: true,
                printCell: row => <SimpleChipElement values={row.original.resourceNames} asPrint/>,
            },
        },
        {
            id: "start",
            header: t("Start"),
            accessorFn: row => row.start,
            cell: ({row}) => <DateElement date={row.original.start}/>,
            size: 170,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true,
                printCell: row => <DateElement date={row.original.start}/>,
            },
        },
        {
            id: "end",
            header: t("End"),
            accessorFn: row => row.end,
            cell: ({row}) => <DateElement date={row.original.end}/>,
            size: 170,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true,
                printCell: row => <DateElement date={row.original.end}/>,
            },
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: row => row.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        createReservationUpdatedColumn<OtherReservationListRowDTO>(t("Reservation updated")),
    ], [ageCategories, queryRange.start, t]);

    const controller = useServerTable<OtherReservationListRowDTO>({
        tableId: "other-reservation-list-v1",
        columns,
        getRowId: row => row.id,
        initialSorting: [{id: "dateTime", desc: true}],
        onRowClick: handleOpenReservation,
        queryDependencies: [
            sectorId,
            queryRange.start.getTime(),
            queryRange.end.getTime(),
            resourceSelection.resources.length,
            resourceSelection.allSelected,
            resourceSelection.selectedIds.join(","),
        ],
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await ReservationAggregationService.listOther({
                sectorId,
                resourceIds: selectedResourceIds,
                search: searchQuery || undefined,
                filters: filterRules.length
                    ? {
                        filterGroups: [{
                            filterRules,
                            operator: DataFilterOperator.AND,
                        }],
                    }
                    : undefined,
                overlaps: {
                    start: startOfDay(queryRange.start),
                    end: endOfDay(queryRange.end),
                },
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, filterRules, sorting}: ServerTableExportQuery,
    ): OtherReservationListRequestDTO => ({
        sectorId,
        resourceIds: selectedResourceIds,
        search: searchQuery || undefined,
        filters: filterRules.length
            ? {
                filterGroups: [{
                    filterRules,
                    operator: DataFilterOperator.AND,
                }],
            }
            : undefined,
        overlaps: {
            start: startOfDay(queryRange.start),
            end: endOfDay(queryRange.end),
        },
        limit: serverTableExportLimit(sorting),
    }), [queryRange.end, queryRange.start, sectorId, selectedResourceIds]);

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <RightSidebarRailHeader>
                            <RightSidebarRailHeaderCenter>
                                <RangeDatePicker
                                    value={dateRange}
                                    onChange={onRangeChange}
                                    showStepButtons
                                    size="medium"
                                />
                            </RightSidebarRailHeaderCenter>
                        </RightSidebarRailHeader>

                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <ServerTableRailFilter
                                options={filterOptionsQuery.data ?? []}
                                disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                            />
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                            <RightSidebarDivider/>
                            <RightSidebarCreateButton
                                label={t("Create reservation")}
                                disabled={readOnly}
                                onClick={handleCreateReservation}
                            />
                            <RightSidebarDivider/>
                            <ReservationResourcePicker
                                selection={resourceSelection}
                                focusedDate={dateRange.start}
                            />
                            <ServerTableRailPrint title={t("Reservations")}/>
                            <ServerTableRailExport
                                list="other-reservations"
                                title={t("Reservations")}
                                buildRequest={exportRequest}
                            />
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>

            <ReservationDialog
                open={Boolean(selectedReservation)}
                reservation={selectedReservation}
                onClose={() => setSelectedReservation(undefined)}
                onSave={() => {
                    setSelectedReservation(undefined);
                    void controller.query.refetch();
                }}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
}
